import {
  AbsoluteFill,
  useCurrentFrame,
  interpolate,
  spring,
  useVideoConfig,
} from 'remotion';

// Components
import { employeeYearInReviewTheme } from '@/components/EmployeeYearInReview/theme';
import { VerifiedIcon } from '@/components/common/VerifiedIcon';
import { TypingText } from '@/components/common/animations/TypingText';
import { AnimatedText } from '@/components/common/AnimatedText';
import { Confetti } from '@/components/common/ConfettiAnimation';

// Fonts
import { lalezarFontFamily, michromaFontFamily } from '@/styles/fonts';

// Utils
import { combineClasses } from '@/utils';
import { formatDate } from '@shared/utils';

// Types
import { AnimationPreset, EmployeeCertification } from '@/types';

// Constants
import { DEFAULT_CONFETTI_COLORS } from '@/components/common/ConfettiAnimation/constants';
import { ANIMATION_SPRING_CONFIGS, CLAMP_BOTH } from '@/constants';
import { CONFETTI_CONFIG_SCENE6, TRANSITION_DURATION } from '../constants';
import { DATE_FORMATS } from '@shared/constants';

// Animation timing offsets (relative to each card's startFrame)
// Bottom-right section starts ~0.5s (15 frames) after top-left NumberSection entry (frame 8)
const BOTTOM_SECTION_DELAY = 23;
const ANIMATION_OFFSET = {
  CERTIFICATE_SECTION_ENTRY: BOTTOM_SECTION_DELAY,
  CERTIFICATE_TITLE_START: BOTTOM_SECTION_DELAY + 10,
  DATE_START: BOTTOM_SECTION_DELAY + 30,
  VERIFIED_ICON_START: BOTTOM_SECTION_DELAY,
} as const;

interface CertificateCardProps {
  certificate: EmployeeCertification;
  startFrame: number;
  transitionStartFrame?: number;
  isFlipped: boolean;
  isLatest?: boolean;
  animationDelay?: number;
}

export const CertificateCard = ({
  certificate,
  startFrame,
  transitionStartFrame,
  isFlipped,
  isLatest = false,
  animationDelay = 0,
}: CertificateCardProps): React.ReactNode => {
  const { colors, typography } = employeeYearInReviewTheme;
  const rawFrame = useCurrentFrame();
  const frame = Math.max(0, rawFrame - animationDelay);
  const { fps, width, height } = useVideoConfig();

  const localFrame = frame - startFrame;

  // Exit animation
  const isExiting =
    transitionStartFrame !== undefined && frame >= transitionStartFrame;
  const exitFrame = isExiting ? frame - transitionStartFrame : 0;
  const exitProgress = isExiting
    ? Math.min(exitFrame / TRANSITION_DURATION, 1)
    : 0;

  // Certificate section (bottom) animations
  const certificateSectionProgress = spring({
    frame: localFrame - ANIMATION_OFFSET.CERTIFICATE_SECTION_ENTRY,
    fps,
    config: ANIMATION_SPRING_CONFIGS[AnimationPreset.SNAPPY],
  });

  // Entry: slide up from bottom
  const certificateEntryTranslateY = interpolate(
    certificateSectionProgress,
    [0, 1],
    [80, 0],
    CLAMP_BOTH,
  );

  const certificateSectionTranslateY = isExiting
    ? 0
    : certificateEntryTranslateY;

  const certificateSectionOpacity = isExiting
    ? 1
    : interpolate(certificateSectionProgress, [0, 0.3], [0, 1], CLAMP_BOTH);

  // Staggered split exit for icon, title, and date
  const iconExitProgress = interpolate(
    exitProgress,
    [0, 0.4],
    [0, 1],
    CLAMP_BOTH,
  );
  const iconExitTranslateY = interpolate(
    iconExitProgress,
    [0, 1],
    [0, -30],
    CLAMP_BOTH,
  );
  const iconExitScale = interpolate(
    iconExitProgress,
    [0, 1],
    [1, 0],
    CLAMP_BOTH,
  );
  const iconExitOpacity = interpolate(
    iconExitProgress,
    [0, 0.6],
    [1, 0],
    CLAMP_BOTH,
  );

  const titleExitProgress = interpolate(
    exitProgress,
    [0.05, 0.8],
    [0, 1],
    CLAMP_BOTH,
  );
  const titleExitTranslateX = interpolate(
    titleExitProgress,
    [0, 1],
    [0, isFlipped ? -200 : 200],
    CLAMP_BOTH,
  );
  const titleExitOpacity = interpolate(
    titleExitProgress,
    [0, 0.6],
    [1, 0],
    CLAMP_BOTH,
  );

  const dateExitProgress = interpolate(
    exitProgress,
    [0.15, 1],
    [0, 1],
    CLAMP_BOTH,
  );
  const dateExitTranslateX = interpolate(
    dateExitProgress,
    [0, 1],
    [0, isFlipped ? 200 : -200],
    CLAMP_BOTH,
  );
  const dateExitOpacity = interpolate(
    dateExitProgress,
    [0, 0.6],
    [1, 0],
    CLAMP_BOTH,
  );

  // Dynamic alignment classes based on isFlipped
  const certificateSectionAlignClass = isFlipped ? 'items-start' : 'items-end';
  const certificateTitleAlignClass = isFlipped ? 'text-left' : 'text-right';

  // Absolute startFrame offsets for sub-animations
  const absoluteTitleStart =
    startFrame + ANIMATION_OFFSET.CERTIFICATE_TITLE_START;
  const absoluteDateStart = startFrame + ANIMATION_OFFSET.DATE_START;
  const absoluteVerifiedStart =
    startFrame + ANIMATION_OFFSET.VERIFIED_ICON_START;

  // Get certificate name and format issue date
  const certificateName = certificate.name ?? '';
  const issueText = formatDate(
    certificate.issue,
    DATE_FORMATS.SHORT_MONTH_DAY_YEAR,
  );

  const titleLength = Math.max(certificateName.length, 1);
  const titleStaggerFrames = Math.min(1.5, Math.max(0.6, 45 / titleLength));

  return (
    <>
      <div className="absolute inset-x-0 bottom-0 pt-20 px-72 pb-25">
        {/* Certificate Section with slide-in from bottom / slide-out */}
        <div
          className={combineClasses(
            colors.textPrimary,
            'flex flex-col pb-20',
            certificateSectionAlignClass,
          )}
          style={{
            transform: `translateY(${certificateSectionTranslateY}px)`,
            opacity: certificateSectionOpacity,
          }}
        >
          <div
            style={{
              ...(isExiting && {
                transform: `translateY(${iconExitTranslateY}px) scale(${iconExitScale})`,
                opacity: iconExitOpacity,
              }),
            }}
          >
            <VerifiedIcon
              size={96}
              strokeWidth={8}
              startFrame={absoluteVerifiedStart}
              badgeDrawDuration={70}
              checkmarkDrawDuration={30}
            />
          </div>

          <p
            className={combineClasses(
              typography.lg,
              certificateTitleAlignClass,
              'max-w-5xl mt-4',
            )}
            style={{
              fontFamily: lalezarFontFamily,
              ...(isExiting && {
                transform: `translateX(${titleExitTranslateX}px)`,
                opacity: titleExitOpacity,
              }),
            }}
          >
            <AnimatedText
              text={certificateName}
              startFrame={absoluteTitleStart}
              staggerFrames={titleStaggerFrames}
              springPreset={AnimationPreset.BOUNCY}
            />
          </p>
          <div
            className={combineClasses(typography['2xs'], colors.textPrimary)}
            style={{
              fontFamily: michromaFontFamily,
              ...(isExiting && {
                transform: `translateX(${dateExitTranslateX}px)`,
                opacity: dateExitOpacity,
              }),
            }}
          >
            <TypingText
              text={issueText}
              startFrame={absoluteDateStart}
              charsPerFrame={2.8}
              cursorBlinkSpeed={10}
            />
          </div>
        </div>
      </div>

      {/* Confetti animation for latest certificate */}
      {isLatest && (
        <AbsoluteFill style={{ pointerEvents: 'none', zIndex: 100 }}>
          {CONFETTI_CONFIG_SCENE6.BURST_LOCATIONS.map(({ x, ratio }, index) => (
            <Confetti
              key={index}
              fireAtFrame={startFrame}
              x={width * x}
              y={0}
              width={width}
              height={height}
              particleCount={Math.floor(
                CONFETTI_CONFIG_SCENE6.PARTICLES_PER_BURST * ratio,
              )}
              angle={CONFETTI_CONFIG_SCENE6.ANGLE}
              spread={CONFETTI_CONFIG_SCENE6.SPREAD}
              ticks={CONFETTI_CONFIG_SCENE6.TICKS}
              gravity={CONFETTI_CONFIG_SCENE6.GRAVITY}
              scalar={CONFETTI_CONFIG_SCENE6.SCALAR}
              drift={CONFETTI_CONFIG_SCENE6.DRIFT}
              startVelocity={CONFETTI_CONFIG_SCENE6.START_VELOCITY}
              decay={CONFETTI_CONFIG_SCENE6.DECAY}
              colors={DEFAULT_CONFETTI_COLORS}
              shapes={CONFETTI_CONFIG_SCENE6.SHAPES}
            />
          ))}
        </AbsoluteFill>
      )}
    </>
  );
};
